/**
 * MetricRanking — Ranking vertical Top 3 con medallas para tarjetas KPI.
 *
 * QUÉ HACE: pinta el Top 3 en una lista vertical (una fila por posición) con la
 *   medalla (🥇, 🥈, 🥉), el número, el nombre, el monto y el porcentaje. El
 *   formato vertical optimiza el espacio en pantallas angostas y evita el podio
 *   horizontal. El nombre se trunca y el resto de la fila queda siempre visible.
 * QUÉ INVOCA: `useReducedMotion` (respeta la preferencia del sistema).
 * QUÉ EXPORTA: MetricRanking, MetricRankingItem.
 * QUÉ NO DEBE HACER: no calcula métricas ni accede a datos.
 */
import type { CSSProperties } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { staggerDelay } from "@/lib/animation/motion";

/** Fila del ranking (ya ordenada: índice 0 = 1.er lugar). */
export interface MetricRankingItem {
  /** Nombre del vendedor. */
  label: string;
  /** Monto formateado ("$29.7M"). */
  valueLabel: string;
  /** Porcentaje del total ("24%"), opcional. */
  percentLabel?: string;
}

interface MetricRankingProps {
  /** Hasta 3 ítems ordenados (1.º, 2.º, 3.º). */
  items: MetricRankingItem[];
  className?: string;
}

/** Medalla por posición (1.º, 2.º, 3.º). */
const MEDALS = ["🥇", "🥈", "🥉"];

export const MetricRanking = ({
  items,
  className = "",
}: MetricRankingProps) => {
  const reducedMotion = useReducedMotion();

  if (items.length === 0) return null;

  return (
    <div className={`flex min-h-0 flex-col gap-1 overflow-hidden ${className}`}>
      {items.map((item, index) => (
        <div
          key={`${item.label}-${index}`}
          title={`${item.label} · ${item.valueLabel}${
            item.percentLabel ? ` (${item.percentLabel})` : ""
          }`}
          className={`flex min-w-0 items-center gap-2${
            reducedMotion ? "" : " animate-chart-fade"
          }`}
          style={
            reducedMotion
              ? undefined
              : ({ animationDelay: staggerDelay(index) } as CSSProperties)
          }
        >
          <span className="shrink-0 text-[9px] leading-none" aria-hidden="true">
            {MEDALS[index] ?? `${index + 1}.`}
          </span>
          <span className="min-w-0 flex-1 truncate text-[9px] font-medium text-gray-700 dark:text-gray-200 sm:text-[10px]">
            {index + 1}. {item.label}
          </span>
          <span className="shrink-0 text-[9px] font-semibold tabular-nums text-gray-600 dark:text-gray-300">
            {item.valueLabel}
            {item.percentLabel && (
              <span className="ml-1 font-normal text-gray-400 dark:text-slate-500">
                ({item.percentLabel})
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};
